import React, { useRef, useEffect } from 'react';
import styled from '@2pulse/responsive';
import { col } from '@app/style';
import { IShop } from '@app/common';
import { Menu, MenuItem, MenuItemHolder } from './Menu';
import { SvgMenuCards, SvgMenuMenus, SvgMenuProducts, SvgMenuRH, SvgMenuSearch, SvgMenuSettings } from '../icons';
import { normalize } from '@app/helpers';
import SvgSignOut from '../icons/SvgSignOut';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { authSlice, appSlice, shopApi } from '@app/store';
import { restaurantDOffice } from './restaurant-d-office';
import { SvgMarket } from '../icons/SvgMarket';
import { ShopSelect } from '../form/selects/features/ShopSelect';
import { useForm } from 'react-hook-form';
import { Form } from '../form';
import { SimpleSelectHandle } from '../form/selects/SimpleSelect';
import { theme } from '@app/theme';
import { useStore } from 'react-redux';
import { RootState } from '@app/store/store';
import { SvgPiggyBank } from '../icons/PiggyBank';
import { SvgUsersRound } from '../icons/UsersRound';

export const Sidebar = () => {
  const store = useStore();
  const dispatch = useAppDispatch();

  const formMethods = useForm<{ shop: IShop | undefined }>({
    defaultValues: (() => {
      const state = store.getState() as RootState;
      const persistedShop = sessionStorage.getItem('selectedShop');
      let shop = state.app.selectedShop;

      if (persistedShop) {
        try {
          shop = JSON.parse(persistedShop) as IShop;
        } catch (error) {
          console.error('Failed to parse selectedShop from sessionStorage', error);
        }
      }

      return {
        shop,
      };
    })(),
  });

  const selectedShop = formMethods.watch('shop');

  const shopSelectRef = useRef<SimpleSelectHandle>(null);

  const handleOpenShopSelect = () => {
    if (shopSelectRef.current) {
      shopSelectRef.current.open();
    }
  };

  useEffect(() => {
    if (selectedShop) {
      sessionStorage.setItem('selectedShop', JSON.stringify(selectedShop));
    }
    dispatch(appSlice.actions.setSelectedShop(selectedShop));
  }, [dispatch, selectedShop]);

  // LE RESTAURANT D'OFFICE (Maxence, 1er nov. 2026 : « si l'utilisateur n'a
  // qu'un seul restaurant, il faut l'auto-sélectionner, même sur les autres
  // pages »). Deux suffisent pour savoir s'il n'y en a qu'un ; la règle est
  // dans `restaurant-d-office.ts`. Poser la valeur du formulaire suffit :
  // l'effet ci-dessus persiste et dépêche comme pour un choix à la main.
  const connecte = useAppSelector((state) => !!state.auth.accessToken);
  const { data: restaurantsVisibles } = shopApi.useReadManyShopsQuery(
    { pagination: { skip: 0, take: 2 }, sort: ['publicName:ASC'] },
    { skip: !connecte },
  );
  useEffect(() => {
    const dOffice = restaurantDOffice(restaurantsVisibles?.data, selectedShop);
    if (dOffice !== undefined) formMethods.setValue('shop', dOffice);
  }, [restaurantsVisibles, selectedShop, formMethods]);

  return (
    <SidebarContainer>
      <Logo source={require('@app/assets/img/logo--icon.png')} />

      <Menu>
        <MenuItem
          label="Produits"
          Icon={SvgMenuProducts}
          activeOnRouteNames={[
            'ProductScreen',
            'TagScreen',
            'IngredientScreen',
            'OptionScreen',
            'VariationScreen',
            'AccountCodeScreen',
          ]}
        />

        <MenuItem
          label="Menus"
          Icon={SvgMenuMenus}
          activeOnRouteNames={['PackScreen', 'StepScreen']}
        />

        <MenuItem
          label="Catalogues"
          Icon={SvgMenuCards}
          activeOnRouteNames={['CatalogScreen', 'CatalogSnapshotScreen', 'CatalogShopScreen', 'ConnectorScreen']}
        />

        <MenuItem label="Recherche" Icon={SvgMenuSearch} activeOnRouteNames={['OrderSearchScreen']} />

        <MenuItem label="Clients" Icon={SvgUsersRound} activeOnRouteNames={['CustomerScreen']} />

        <MenuItem label="Finances" Icon={SvgPiggyBank} activeOnRouteNames={['ServiceScreen', 'FiscalScreen']} />

        <MenuItem label="RH" Icon={SvgMenuRH} activeOnRouteNames={['TimesheetScreen']} />

        <MenuItem
          label="Réglages"
          Icon={SvgMenuSettings}
          activeOnRouteNames={[
            'ShopScreen',
            'PaymentMethodScreen',
            'PaymentProviderScreen',
            'PromotionScreen',
            'PublicityScreen',
            'OrderCancelReasonScreen',
            'KitchenAreaScreen',
            'OrganizationScreen',
            'UserScreen',
            'RoleScreen',
            'LoyaltyScreen',
            'DeliveryZoneScreen',
          ]}
        />
      </Menu>

      <Form formMethods={formMethods}>
        <ShopSelect name="shop" label="Choisissez un restaurant" hidden ref={shopSelectRef} />
      </Form>

      <SignOut>
        <MenuItemHolder onPress={handleOpenShopSelect}>
          <MarketIconWrapper>
            <SvgMarket
              width={normalize(34)}
              height={normalize(34)}
              fill={selectedShop ? theme.colors.celadonblue : theme.colors.babypowder}
            />
            {selectedShop && (
              <MarketBadge>
                <MarketBadgeText>!</MarketBadgeText>
              </MarketBadge>
            )}
          </MarketIconWrapper>
        </MenuItemHolder>

        <MenuItemHolder onPress={() => dispatch(authSlice.actions.logout())}>
          <SvgSignOut />
        </MenuItemHolder>
      </SignOut>
    </SidebarContainer>
  );
};

const SidebarContainer = styled.View(({ theme }) => ({
  zIndex: 999,
  position: 'absolute',
  left: 0,
  top: '2%',
  height: '96%',
  width: theme.sizes.sidebarWidth,
  borderTopRightRadius: 21,
  borderBottomRightRadius: 21,
  backgroundColor: theme.colors.oxfordblue,
  shadowColor: theme.colors.oxfordblue,
  shadowOffset: {
    width: 0,
    height: 2,
  },
  shadowOpacity: 0.55,
  shadowRadius: 8,
  elevation: 18,
  ...col,
  justifyContent: 'center',
  alignItems: 'center',
}));

const Logo = styled.Image({
  position: 'absolute',
  top: normalize(34),
  width: normalize(55),
  height: normalize(55),
  resizeMode: 'contain',
});

const SignOut = styled.View({
  position: 'absolute',
  bottom: normalize(34),
});

const MarketIconWrapper = styled.View({
  position: 'relative',
  justifyContent: 'center',
  alignItems: 'center',
});

const MarketBadge = styled.View(({ theme }) => ({
  position: 'absolute',
  top: -4,
  right: -4,
  minWidth: normalize(16),
  height: normalize(16),
  borderRadius: normalize(8),
  backgroundColor: theme.colors.gold,
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10,
  paddingHorizontal: 3,
}));

const MarketBadgeText = styled.Text(({ theme }) => ({
  color: theme.colors.oxfordblue,
  fontWeight: 'bold',
  fontSize: normalize(10),
  textAlign: 'center',
}));
